/**
 * mapListing.js
 *
 * Transformiert ein Raw-Listing-Objekt (API-Response) in das App-interne Format.
 *
 * Hinweis: scoreToVerdict und scoreToTier sind interne Hilfsfunktionen.
 * Sie werden nicht mehr exportiert — Verdicts werden in Komponenten via useRules() aufgelöst.
 * verdictSentence wurde entfernt — kommt aus rules.verdicts[x].verdict_sentence via useRules().
 */

const PROPERTY_TYPE_DE = { House: 'Haus', Apartment: 'Wohnung', Etagenwohnung: 'Wohnung' };
function translateType(t) { return PROPERTY_TYPE_DE[t] || t || 'Objekt'; }

/**
 * Internes Score-zu-Key-Mapping — wird für vk (Verdict Key) genutzt.
 * Konsumenten die den Label brauchen sollen useRules() + getVerdictFromRules() verwenden.
 */
function scoreToVerdictKey(score) {
  if (score === null || score === undefined) return 'good';
  if (score >= 80) return 'top';
  if (score >= 65) return 'good';
  if (score >= 50) return 'check';
  if (score >= 35) return 'risky';
  return 'pass';
}

/**
 * Internes Score-zu-Tier-Mapping — wird für CSS-Klassen (tier-top/mid/weak) genutzt.
 */
function scoreToTierInternal(score) {
  if (score === null || score === undefined) return 'mid';
  if (score >= 80) return 'top';
  if (score >= 65) return 'mid';
  if (score >= 50) return 'mid';
  if (score >= 35) return 'weak';
  return 'weak';
}

export function mapListing(l) {
  const score = l.Score ?? null;
  const typeDe = translateType(l.PropertyType);
  const customName = l.CustomName || null;
  const resolvedAddress = l.Address
    || (l.Street ? [l.Street, l.HouseNumber].filter(Boolean).join(' ') : null)
    || `${typeDe} in ${l.City || 'unbekannt'}`;
  return {
    id:            l.ListingID,
    customName,
    address:       customName || resolvedAddress,
    rawAddress:    resolvedAddress,
    city:          [l.City, l.District, l.State].filter(Boolean).join(', ') || '',
    score:         score,
    vk:            scoreToVerdictKey(score),
    tier:          scoreToTierInternal(score),
    price:         parseFloat(l.PurchasePrice) || 0,
    sqm:           parseFloat(l.Area) || 0,
    rooms:         l.Rooms || null,
    cf:            l.CashflowMonatlich !== null && l.CashflowMonatlich !== undefined ? parseFloat(l.CashflowMonatlich) : null,
    ekRendite:     l.EkRendite !== null && l.EkRendite !== undefined ? parseFloat(l.EkRendite) : null,
    eq:            l.EquityRatio ? Math.round(l.EquityRatio * 100) : 20,
    signals:       (() => { try { return Array.isArray(l.Signals) ? l.Signals : JSON.parse(l.Signals || '[]'); } catch { return []; } })(),
    confirmedByUser: !!l.ConfirmedByUser,
    confirmedAt:   l.ConfirmedAt || null,
    listingUrl:    l.ListingURL || null,
    source:        l.Source || 'Immoscout',
    propertyType:  l.PropertyType || '',
    street:        l.Street || null,
    houseNumber:   l.HouseNumber || null,
    postalCode:    l.PostalCode || null,
    district:      l.District || null,
    state:         l.State || null,
    usableArea:    l.UsableArea ? parseFloat(l.UsableArea) : null,
    landArea:      l.LandArea ? parseFloat(l.LandArea) : null,
    bedrooms:      l.Bedrooms || null,
    bathrooms:     l.Bathrooms || null,
    numberOfFloors: l.NumberOfFloors || null,
    buildingType:  l.BuildingType || null,
    propertyCondition: l.PropertyCondition || null,
    renovation:    l.Renovation || null,
    qualityOfEquipment: l.QualityOfEquipment || null,
    createdAt:     l.CreatedAt || null,
    ppSqm:         l.PricePerSquareMeter ? parseFloat(l.PricePerSquareMeter) : null,
    rent:          l.BaseRent ? parseFloat(l.BaseRent) : null,
    ctaLabel:      'Objekt öffnen →',
  };
}

/**
 * Löst das passende Verdict-Objekt aus dem Rules-Objekt für einen Score auf.
 * Gibt das erste Verdict zurück dessen Range passt.
 *
 * @param {Object} rules - Rules-Objekt aus useRules()
 * @param {number|null} score
 * @returns {{ key, label, color_token, verdict_sentence } | null}
 */
export function getVerdictFromRules(rules, score) {
  if (score === null || score === undefined || !rules?.verdicts) return null;
  return rules.verdicts.find(
    (v) => score >= v.score_min && score <= v.score_max
  ) ?? null;
}
